import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 描述样式
const Description = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  marginBottom: '2rem',
  maxWidth: '800px'
});

// 筛选器容器样式
const FiltersContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '1rem',
  padding: '1.5rem',
  background: '$backgroundSecondary',
  borderRadius: '12px',
  marginBottom: '2rem'
});

// 筛选器组样式
const FilterGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  minWidth: '200px'
});

// 筛选器标签样式
const FilterLabel = styled('label', {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: '$textSecondary'
});

// 筛选器选择框样式
const FilterSelect = styled('select', {
  padding: '0.5rem',
  borderRadius: '6px',
  border: '1px solid $borderColor',
  background: '$backgroundPrimary',
  color: '$textPrimary',
  fontSize: '0.9rem',
  
  '&:focus': {
    outline: 'none',
    borderColor: '$accentPrimary'
  }
});

// 产品网格容器样式
const ProductsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '2rem'
});

// 产品卡片样式
const ProductCard = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)'
  }
});

// 产品图片容器样式
const ProductImageContainer = styled('div', {
  width: '100%',
  height: '200px',
  background: '$backgroundTertiary',
  position: 'relative',
  overflow: 'hidden'
});

// 产品图片样式
const ProductImage = styled('div', {
  width: '100%',
  height: '100%',
  background: 'url("/assets/placeholder-miner.jpg") center/cover no-repeat',
  transition: 'transform 0.5s ease',
  
  '&:hover': {
    transform: 'scale(1.05)'
  }
});

// 产品标签样式
const ProductBadge = styled('span', {
  position: 'absolute',
  top: '10px',
  right: '10px',
  padding: '0.25rem 0.75rem',
  borderRadius: '20px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  zIndex: 1,
  
  variants: {
    variant: {
      new: {
        background: '$accentPrimary',
        color: 'white'
      },
      popular: {
        background: '$warning',
        color: 'white'
      },
      limited: {
        background: '$danger',
        color: 'white'
      }
    }
  }
});

// 产品内容容器样式
const ProductContent = styled('div', {
  padding: '1.5rem'
});

// 产品名称样式
const ProductName = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 产品说明样式
const ProductDescription = styled('p', {
  fontSize: '0.9rem',
  color: '$textSecondary',
  marginBottom: '1rem',
  lineHeight: 1.5
});

// 产品规格容器样式
const SpecsContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '0.75rem',
  marginBottom: '1.5rem'
});

// 产品规格项样式
const SpecItem = styled('div', {
  display: 'flex',
  flexDirection: 'column'
});

// 产品规格标签样式
const SpecLabel = styled('span', {
  fontSize: '0.75rem',
  color: '$textTertiary',
  marginBottom: '0.25rem'
});

// 产品规格值样式
const SpecValue = styled('span', {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 产品价格容器样式
const PriceContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '1rem',
  paddingTop: '1rem',
  borderTop: '1px solid $borderColor'
});

// 产品价格样式
const Price = styled('div', {
  display: 'flex',
  flexDirection: 'column'
});

// 产品价格标签样式
const PriceLabel = styled('span', {
  fontSize: '0.8rem',
  color: '$textTertiary'
});

// 产品价格值样式
const PriceValue = styled('span', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 按钮样式
const Button = styled('button', {
  padding: '0.5rem 1rem',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: '$accentPrimary',
        color: 'white',
        border: 'none',
        '&:hover': {
          opacity: 0.9
        }
      },
      secondary: {
        background: 'transparent',
        color: '$accentPrimary',
        border: '1px solid $accentPrimary',
        '&:hover': {
          background: 'rgba(6, 214, 160, 0.05)'
        }
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 分页容器样式
const PaginationContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '3rem'
});

// 分页按钮样式
const PaginationButton = styled('button', {
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '6px',
  border: '1px solid $borderColor',
  background: 'transparent',
  color: '$textSecondary',
  fontSize: '0.9rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  '&:hover': {
    borderColor: '$accentPrimary',
    color: '$accentPrimary'
  },
  
  variants: {
    active: {
      true: {
        background: '$accentPrimary',
        color: 'white',
        borderColor: '$accentPrimary',
        
        '&:hover': {
          opacity: 0.9,
          color: 'white'
        }
      }
    },
    disabled: {
      true: {
        opacity: 0.5,
        cursor: 'not-allowed',
        
        '&:hover': {
          borderColor: '$borderColor',
          color: '$textSecondary'
        }
      }
    }
  }
});

/**
 * ASIC矿机产品列表页面组件
 * 展示可购买的矿机产品及筛选功能
 */
const AsicShopProducts: React.FC = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  
  // 假设的筛选状态，实际项目中可以用useReducer或专门的状态管理
  const [filters, setFilters] = useState({
    manufacturer: 'all',
    algorithm: 'all',
    priceRange: 'all'
  });
  
  // 处理筛选器改变
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // 重置到第一页
    setCurrentPage(1);
  };
  
  // 矿机数据（模拟数据）
  const miners = [
    {
      id: 1,
      name: 'Antminer S21 Pro',
      description: '比特大陆最新旗舰矿机，采用最先进的芯片工艺，提供高达230 TH/s的算力',
      image: '/assets/miner-s21.jpg',
      badge: 'new',
      specs: {
        hashrate: '230 TH/s',
        power: '3680W',
        efficiency: '16.0 J/TH',
        algorithm: 'SHA-256'
      },
      price: 9999,
      currency: 'USDT',
      manufacturer: 'Bitmain'
    },
    {
      id: 2,
      name: 'Whatsminer M50S',
      description: '迈克瑞科技高性能矿机，平衡的功耗比和稳定性，适合长期挖矿',
      image: '/assets/miner-m50s.jpg',
      badge: 'popular',
      specs: {
        hashrate: '126 TH/s',
        power: '3276W',
        efficiency: '26.0 J/TH',
        algorithm: 'SHA-256'
      },
      price: 5499,
      currency: 'USDT',
      manufacturer: 'MicroBT'
    },
    {
      id: 3,
      name: 'Avalon A1366',
      description: '嘉楠耘智中端矿机，良好的稳定性和可靠性，性价比高',
      image: '/assets/miner-a1366.jpg',
      badge: '',
      specs: {
        hashrate: '72 TH/s',
        power: '2880W',
        efficiency: '40.0 J/TH',
        algorithm: 'SHA-256'
      },
      price: 2999,
      currency: 'USDT',
      manufacturer: 'Canaan'
    },
    {
      id: 4,
      name: 'KS5 Pro',
      description: '比特小鹿高性能矿机，专为ETH挖矿优化，带来卓越的挖矿效率',
      image: '/assets/miner-ks5.jpg',
      badge: 'limited',
      specs: {
        hashrate: '5 GH/s',
        power: '2450W',
        efficiency: '0.49 J/MH',
        algorithm: 'Ethash'
      },
      price: 7599,
      currency: 'USDT',
      manufacturer: 'iPollo'
    },
    {
      id: 5,
      name: 'Antminer E9 Pro',
      description: '比特大陆ETH系列矿机，高算力保证稳定收益',
      image: '/assets/miner-e9.jpg',
      badge: '',
      specs: {
        hashrate: '3.6 GH/s',
        power: '2560W',
        efficiency: '0.71 J/MH',
        algorithm: 'Ethash'
      },
      price: 6899,
      currency: 'USDT',
      manufacturer: 'Bitmain'
    },
    {
      id: 6,
      name: 'Jasminer X4',
      description: '小蚂蚁科技ETC专用矿机，低功耗设计，家用挖矿理想选择',
      image: '/assets/miner-x4.jpg',
      badge: '',
      specs: {
        hashrate: '520 MH/s',
        power: '240W',
        efficiency: '0.46 J/MH',
        algorithm: 'Etchash'
      },
      price: 1999,
      currency: 'USDT',
      manufacturer: 'Jasminer'
    }
  ];
  
  // 处理分页
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // 渲染分页按钮
  const renderPagination = () => {
    const totalPages = 2; // 假设共有2页
    
    return (
      <PaginationContainer>
        <PaginationButton 
          disabled={currentPage === 1}
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
        >
          &lt;
        </PaginationButton>
        
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <PaginationButton
            key={page}
            active={currentPage === page}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </PaginationButton>
        ))}
        
        <PaginationButton
          disabled={currentPage === totalPages}
          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
        >
          &gt;
        </PaginationButton>
      </PaginationContainer>
    );
  };
  
  return (
    <PageContainer>
      <Title>{t('asicShop.products.title', 'ASIC矿机产品')}</Title>
      <Description>
        {t('asicShop.products.description', '浏览我们的高性能ASIC矿机产品，从市场领先的制造商精选而来。所有产品均经过专业测试，确保最佳性能。')}
      </Description>
      
      {/* 筛选器区域 */}
      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="manufacturer-filter">{t('asicShop.filters.manufacturer', '制造商')}</FilterLabel>
          <FilterSelect 
            id="manufacturer-filter"
            name="manufacturer" 
            value={filters.manufacturer} 
            onChange={handleFilterChange}
            aria-label={t('asicShop.filters.manufacturer', '制造商')}
            title={t('asicShop.filters.manufacturer', '制造商')}
          >
            <option value="all">{t('asicShop.filters.all', '全部')}</option>
            <option value="bitmain">Bitmain</option>
            <option value="microbt">MicroBT</option>
            <option value="canaan">Canaan</option>
            <option value="ipollo">iPollo</option>
            <option value="jasminer">Jasminer</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel htmlFor="algorithm-filter">{t('asicShop.filters.algorithm', '算法')}</FilterLabel>
          <FilterSelect 
            id="algorithm-filter"
            name="algorithm" 
            value={filters.algorithm} 
            onChange={handleFilterChange}
            aria-label={t('asicShop.filters.algorithm', '算法')}
            title={t('asicShop.filters.algorithm', '算法')}
          >
            <option value="all">{t('asicShop.filters.all', '全部')}</option>
            <option value="sha256">SHA-256 (BTC)</option>
            <option value="ethash">Ethash (ETH)</option>
            <option value="etchash">Etchash (ETC)</option>
            <option value="scrypt">Scrypt (LTC)</option>
            <option value="randomx">RandomX (XMR)</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel htmlFor="price-filter">{t('asicShop.filters.priceRange', '价格范围')}</FilterLabel>
          <FilterSelect 
            id="price-filter"
            name="priceRange" 
            value={filters.priceRange} 
            onChange={handleFilterChange}
            aria-label={t('asicShop.filters.priceRange', '价格范围')}
            title={t('asicShop.filters.priceRange', '价格范围')}
          >
            <option value="all">{t('asicShop.filters.all', '全部')}</option>
            <option value="0-2000">$0 - $2,000</option>
            <option value="2000-5000">$2,000 - $5,000</option>
            <option value="5000-10000">$5,000 - $10,000</option>
            <option value="10000+">$10,000+</option>
          </FilterSelect>
        </FilterGroup>
      </FiltersContainer>
      
      {/* 产品网格 */}
      <ProductsGrid>
        {miners.map(miner => (
          <ProductCard key={miner.id}>
            <ProductImageContainer>
              <ProductImage style={{ background: `url(${miner.image || '/assets/placeholder-miner.jpg'}) center/cover no-repeat` }} />
              {miner.badge && (
                <ProductBadge 
                  variant={
                    miner.badge === 'new' ? 'new' : 
                    miner.badge === 'popular' ? 'popular' : 
                    miner.badge === 'limited' ? 'limited' : 
                    undefined
                  }
                >
                  {t(`asicShop.badges.${miner.badge}`, miner.badge)}
                </ProductBadge>
              )}
            </ProductImageContainer>
            
            <ProductContent>
              <ProductName>{miner.name}</ProductName>
              <ProductDescription>{miner.description}</ProductDescription>
              
              <SpecsContainer>
                <SpecItem>
                  <SpecLabel>{t('asicShop.specs.hashrate', '算力')}</SpecLabel>
                  <SpecValue>{miner.specs.hashrate}</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>{t('asicShop.specs.power', '功耗')}</SpecLabel>
                  <SpecValue>{miner.specs.power}</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>{t('asicShop.specs.efficiency', '能效比')}</SpecLabel>
                  <SpecValue>{miner.specs.efficiency}</SpecValue>
                </SpecItem>
                <SpecItem>
                  <SpecLabel>{t('asicShop.specs.algorithm', '算法')}</SpecLabel>
                  <SpecValue>{miner.specs.algorithm}</SpecValue>
                </SpecItem>
              </SpecsContainer>
              
              <PriceContainer>
                <Price>
                  <PriceLabel>{t('asicShop.price', '价格')}</PriceLabel>
                  <PriceValue>{miner.price} {miner.currency}</PriceValue>
                </Price>
                
                <Link to={`/products/asic-shop/details?id=${miner.id}`}>
                  <Button variant="primary">
                    {t('asicShop.viewDetails', '查看详情')}
                  </Button>
                </Link>
              </PriceContainer>
            </ProductContent>
          </ProductCard>
        ))}
      </ProductsGrid>
      
      {/* 分页 */}
      {renderPagination()}
    </PageContainer>
  );
};

export default AsicShopProducts; 